import React, { useCallback, useRef, useState } from 'react'
import type { PlaylistId } from 'types/playlist'
import type { EventSource } from 'types/ui'
import QueuePopup from 'components/common/player/player-queue/queue-popup'
import Button from 'components/ui/button'
import useClickOutside from 'hooks/use-click-outside'
import styles from './index.module.css'

type Props = {
  mediaSource: EventSource
  trackGroupId: number | null | undefined
  trackId: number
  trackPlaylistId: PlaylistId | null | undefined
  trackShortlistId: string | null | undefined
}

/**
 * PlayerQueue component for displaying and managing the playback queue
 *
 * Provides a toggle button to show/hide the queue popup that displays the current
 * playback queue. Handles click-outside behavior to close the popup and manages
 * the queue state for different media sources.
 *
 * @param props - Component props
 * @param props.mediaSource - Source of the media (playlist, album, etc.)
 * @param props.trackGroupId - ID of the track group
 * @param props.trackId - ID of the current track
 * @param props.trackPlaylistId - ID of the playlist containing the track
 * @param props.trackShortlistId - ID of the shortlist containing the track
 * @returns Memoized JSX element containing the queue button and popup
 */
const PlayerQueue = ({
  mediaSource,
  trackGroupId,
  trackId,
  trackPlaylistId,
  trackShortlistId,
}: Props) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const toggleQueuePopup = useCallback(() => {
    setIsOpen(isOpen => !isOpen)
  }, [])
  useClickOutside(containerRef, () => {
    setIsOpen(false)
  })
  return (
    <div className={styles.playerQueue} ref={containerRef}>
      <QueuePopup
        mediaSource={mediaSource}
        open={isOpen}
        trackGroupId={trackGroupId}
        trackId={trackId}
        trackPlaylistId={trackPlaylistId}
        trackShortlistId={trackShortlistId}
      />
      <Button
        appearance="icon"
        icon="player-playlist"
        onClick={toggleQueuePopup}
      />
    </div>
  )
}

export default React.memo<Props>(PlayerQueue)
